export type LegalSection = {
  heading: string;
  body: string[];
  /** Phrases in the body that should become external links */
  links?: { text: string; href: string }[];
  /** Internal links shown under the section (e.g. full credit pages) */
  creditLinks?: { label: string; href: string }[];
};

export type LegalDoc = {
  slug: string;
  label: string;
  title: string;
  /** Instrument Serif italic tail of the title */
  titleAccent: string;
  intro: string;
  updated: string;
  sections: LegalSection[];
};

export const legalDocs: LegalDoc[] = [
  {
    slug: 'imprint',
    label: 'Imprint',
    title: 'Imprint',
    titleAccent: 'and legal notice',
    intro: 'Information about the operator of this website, provided in line with applicable disclosure requirements.',
    updated: '20 October 2026',
    sections: [
      {
        heading: 'Operator',
        body: [
          'KAYS VENTURES FZCO, operating as KAYS & KAYS (the "Company")',
          'Registered office: Dubai Silicon Oasis, DDP, Building A1, Dubai, United Arab Emirates',
        ],
      },
      {
        heading: 'Managing Director',
        body: ['Adrian Kays'],
      },
      {
        heading: 'Contact',
        body: ['Phone: +971 50 525 4282', 'Email: info@kaysandkays.com', 'Media enquiries: media@kaysandkays.com'],
      },
      {
        heading: 'Registration',
        body: [
          'Trade licence no.: 16239',
          'Regulatory authority: IFZA — International Free Zone Authority, Dubai',
          'VAT identification no.: application pending',
        ],
      },
      {
        heading: 'Responsible for content',
        body: ['Adrian Kays, at the address above.'],
      },
      {
        heading: 'Nature of this website',
        body: [
          'KAYS & KAYS is a private family office. This website is provided for general information only. It does not constitute an offer, solicitation or recommendation to buy or sell any security, financial instrument or investment product, and it is not directed at any person in a jurisdiction where such communication would be unlawful.',
        ],
      },
      {
        heading: 'Liability for content',
        body: [
          'We prepare the content of this website with care, but cannot guarantee that it is complete, accurate or current at all times. We are not obliged to monitor third-party information transmitted or stored on this website.',
        ],
      },
      {
        heading: 'Liability for links',
        body: [
          'This website links to external websites, including those of companies in our ecosystem. We have no control over their content and accept no responsibility for it. The respective provider or operator is always responsible for the content of linked pages.',
        ],
      },
      {
        heading: 'Copyright',
        body: [
          'All texts, images, graphics and design elements on this website are the property of KAYS & KAYS or used with permission. Any reproduction, editing or distribution outside the limits of copyright law requires our prior written consent.',
        ],
      },
      {
        heading: 'Creator credentials',
        body: [
          'Brand strategy, concept, design and development of the KAYS & KAYS brand and this website by Julius Branding (juliusbranding.com).',
          'Brand strategy, concept, design and development of the KAYS & KAYS brand and this website by Syne, a design, artificial intelligence and technology agency (syne.digital).',
          '100% made with AI in Germany.',
        ],
        links: [
          { text: 'Julius Branding', href: 'https://juliusbranding.com/' },
          { text: 'juliusbranding.com', href: 'https://juliusbranding.com/' },
          { text: 'Syne', href: 'https://syne.digital/' },
          { text: 'syne.digital', href: 'https://syne.digital/' },
        ],
      },
    ],
  },
  {
    slug: 'privacy',
    label: 'Privacy Policy',
    title: 'Privacy',
    titleAccent: 'policy',
    intro:
      'How KAYS & KAYS collects, uses and protects personal data when you visit this website or get in touch with us. We collect as little as we can, and keep it no longer than we need to.',
    updated: '20 October 2026',
    sections: [
      {
        heading: 'Who is responsible',
        body: [
          'The controller for the processing of personal data on this website is KAYS VENTURES FZCO (KAYS & KAYS), Dubai Silicon Oasis, DDP, Building A1, Dubai, United Arab Emirates. You can reach us about any privacy matter at privacy@kaysandkays.com.',
          'We process personal data in line with the UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data and, where it applies to visitors in the European Economic Area or Switzerland, the EU General Data Protection Regulation (GDPR) and the Swiss Federal Act on Data Protection.',
        ],
      },
      {
        heading: 'Data we collect',
        body: [
          'Visiting the website: when you open this website, our hosting provider automatically processes technical data such as your IP address, browser type, operating system, referring page and the date and time of access. This is necessary to deliver the website securely.',
          'Contact form: if you write to us, we process your name, email address, the topic you choose and any message you include.',
          'Email: if you email us directly, we process your address and the content of your message.',
          'Cookies: see the Cookie Policy for details of the small files we use and the choices you have.',
        ],
      },
      {
        heading: 'Why we use it',
        body: [
          'To provide, secure and improve this website — based on our legitimate interest in operating a reliable website.',
          'To answer your enquiry and continue any conversation you start — based on your request and, where relevant, steps towards a business relationship.',
          'To understand, in aggregate, how the website is used — only with your consent, given through the cookie notice.',
          'To meet legal and regulatory obligations that apply to us.',
        ],
      },
      {
        heading: 'Sharing',
        body: [
          'We do not sell personal data. We share it only with service providers who help us run this website and our correspondence (such as hosting and email providers), under agreements that oblige them to protect it, and with authorities where the law requires it.',
          'Some providers may process data outside the UAE or your home country. Where this happens, we rely on appropriate safeguards such as standard contractual clauses or adequacy decisions.',
        ],
      },
      {
        heading: 'How long we keep it',
        body: [
          'Server logs are deleted after no more than 30 days. Enquiries are kept for as long as needed to respond and for up to 24 months afterwards, unless a longer period is required by law or an ongoing relationship.',
        ],
      },
      {
        heading: 'Your rights',
        body: [
          'Depending on where you live, you may request access to your data, its correction or deletion, restriction of processing, data portability, or object to processing based on legitimate interests. Where processing relies on consent, you may withdraw it at any time without affecting earlier processing.',
          'To exercise any of these rights, write to privacy@kaysandkays.com. You also have the right to lodge a complaint with a supervisory authority, such as the UAE Data Office or the authority in your country of residence.',
        ],
      },
      {
        heading: 'Security',
        body: [
          'This website is served over an encrypted connection (HTTPS). We use technical and organisational measures appropriate to the sensitivity of the data we hold, and limit access to those who need it.',
        ],
      },
      {
        heading: 'Changes',
        body: [
          'We may update this policy as our website or the law changes. The current version is always published on this page, with the date of the latest revision above.',
        ],
      },
    ],
  },
  {
    slug: 'cookies',
    label: 'Cookie Policy',
    title: 'Cookie',
    titleAccent: 'policy',
    intro: 'Which cookies this website uses, why, and how you can control them.',
    updated: '20 October 2026',
    sections: [
      {
        heading: 'What cookies are',
        body: [
          'Cookies are small text files stored on your device when you visit a website. Similar technologies, such as local storage, work in the same way. We refer to all of them as cookies.',
        ],
      },
      {
        heading: 'Strictly necessary',
        body: [
          'These keep the website working and secure, and remember your cookie choice. They are always active and do not require consent.',
          'cookie-consent — stores whether you accepted or declined optional cookies. Duration: 12 months.',
        ],
      },
      {
        heading: 'Analytics (optional)',
        body: [
          'With your consent, we use privacy-friendly analytics to understand how many people visit and which pages are read, in aggregate. IP addresses are anonymised and no profiles are built. These cookies are only set after you choose Accept.',
        ],
      },
      {
        heading: 'What we do not use',
        body: ['We do not use advertising, retargeting or social media tracking cookies.'],
      },
      {
        heading: 'Your choices',
        body: [
          'You can accept or decline optional cookies in the notice shown on your first visit. To change your choice later, clear the cookies for this site in your browser and the notice will appear again. You can also block or delete cookies in your browser settings; the website will still work without optional cookies.',
        ],
      },
      {
        heading: 'Contact',
        body: ['Questions about cookies can be sent to privacy@kaysandkays.com. See the Privacy Policy for more on how we handle personal data.'],
      },
    ],
  },
  {
    slug: 'terms',
    label: 'Terms of Use',
    title: 'Terms',
    titleAccent: 'of use',
    intro: 'The conditions under which you may use this website. By accessing it, you agree to these terms.',
    updated: '20 October 2026',
    sections: [
      {
        heading: 'Purpose of the website',
        body: [
          'This website presents KAYS & KAYS, a Dubai-registered family office, and the ventures in its ecosystem. It is intended for general information only.',
        ],
      },
      {
        heading: 'No offer or advice',
        body: [
          'Nothing on this website is an offer, invitation or recommendation to invest, nor investment, legal, tax or other professional advice. KAYS & KAYS does not accept capital from the public. Any decision you make on the basis of this website is your own responsibility.',
        ],
      },
      {
        heading: 'Forward-looking statements',
        body: [
          'Statements about plans, intentions or future developments reflect our views at the time of writing and involve uncertainty. Actual outcomes may differ materially.',
        ],
      },
      {
        heading: 'Intellectual property',
        body: [
          'The names KAYS & KAYS, the logo, the 0/1 Manuscript and all content on this website are protected by intellectual property rights. You may view and print pages for personal, non-commercial use. Any other use requires our prior written permission.',
        ],
      },
      {
        heading: 'Acceptable use',
        body: [
          'You agree not to misuse this website, including by attempting to gain unauthorised access, interfering with its operation, or using automated means to extract content.',
        ],
      },
      {
        heading: 'Third-party websites',
        body: [
          'Links to other websites, including ecosystem companies, are provided for convenience. Each operates under its own terms and privacy policy, for which KAYS & KAYS is not responsible.',
        ],
      },
      {
        heading: 'Limitation of liability',
        body: [
          'To the extent permitted by law, KAYS & KAYS is not liable for any loss arising from the use of, or inability to use, this website or its content. The website is provided "as is", without warranties of any kind.',
        ],
      },
      {
        heading: 'Governing law',
        body: [
          'These terms are governed by the laws of the Emirate of Dubai and the federal laws of the United Arab Emirates. The courts of Dubai have exclusive jurisdiction, unless mandatory consumer protection law in your country provides otherwise.',
        ],
      },
      {
        heading: 'Changes',
        body: ['We may revise these terms at any time. The version published on this page applies from the date shown above.'],
      },
    ],
  },
];
